export const ONLINE_HUB_CATEGORY_ORDER = [
  "beauty",
  "aesthetics",
  "injectables-advanced",
  "vtct-online-theory",
  "bundles",
] as const;

export type OnlineHubCategory = (typeof ONLINE_HUB_CATEGORY_ORDER)[number];

export const ONLINE_PATHWAY_ORDER = [
  "theory",
  "case_study",
  // "live",
] as const;

export type OnlineHubPathway = (typeof ONLINE_PATHWAY_ORDER)[number];

export const onlineCoursesHubContent = {
  seo: {
    title: "Online Aesthetic Courses UK | CPD and VTCT Theory Training",
    description:
      "Online aesthetic, beauty and injectables theory courses. Start today, study at your own pace and earn a CPD or VTCT theory certificate.",
  },
  hero: {
    eyebrow: "On-demand e-learning",
    title: "Online Aesthetic and Beauty Courses",
    description:
      "Build treatment knowledge with tutor-led online training that fits around work, clients and life. Choose theory-only learning or a tutor-reviewed case study route, then speak to admissions if you need practical qualification training.",
    primaryCta: {
      href: "#featured-online-training",
      label: "Browse popular online courses",
    },
    secondaryCta: {
      href: "/request-a-callback/",
      label: "Speak to admissions",
    },
    highlights: [
      "Start today - instant access after checkout",
      "CPD & VTCT theory certificates on completion",
    ],
    stats: [
      {
        value: "Same-day start",
        label: "Access your course as soon as checkout is complete",
      },
      {
        value: "Practitioner-led",
        label: "Lessons built by tutors who deliver onsite training every week",
      },
      {
        value: "4.8/5",
        label: "Rated by 1,200+ graduates across beauty and aesthetics",
      },
    ],
  },
  trainingTypes: {
    eyebrow: "Training pathways",
    title: "Two ways to train online",
    description:
      "Many courses offer more than one route. Pick the level that matches what you need now, from quick theory refresh to tutor-reviewed case study work.",
    types: [
      {
        id: "theory",
        title: "Theory only",
        eyebrow: "Best starting point",
        description:
          "On-demand lessons you can start the same day. Ideal for CPD refresh, VTCT theory, or building confidence before practical training.",
        includes: [
          "Study at your own pace",
          "CPD or VTCT theory certificate on completion",
          "No case study portfolio required",
        ],
        ctaLabel: "Browse theory-only courses",
        meta_title: "Theory-only Online Training Courses | Cosmetic College",
        meta_description:
          "Browse theory-only online training with access while your course is active, CPD certificates and VTCT theory options. Access ends once you complete the course. Many courses also offer tutor-reviewed pathway upgrades.",
      },
      {
        id: "case_study",
        title: "With case study",
        eyebrow: "Tutor-reviewed pathway",
        description:
          "Everything in theory, plus structured portfolio work reviewed by our tutors before certification. Compare tier pricing on each course card - theory-only entry points and tutor-reviewed upgrades are shown side by side.",
        includes: [
          "Full on-demand theory access while your course is active (access ends once you complete the course)",
          "Online portfolio or case study submission with consultation notes and supporting evidence where required",
          "Tutor review and feedback typically within 5 working days",
          "Certificate wording reflects tutor-reviewed completion, not independent treatment sign-off",
          "Optional training kits on selected courses",
        ],
        ctaLabel: "Browse case study courses",
        meta_title: "Online Case Study Training Courses | Cosmetic College",
        meta_description:
          "Browse online training with tutor-reviewed case study pathways, portfolio submission and certification support. Compare tier pricing on each course card.",
      },
      // {
      //   id: "live",
      //   title: "With live sessions",
      //   eyebrow: "Full online pathway",
      //   description:
      //     "The complete route when you want online learning plus scheduled webinar and assessment support. Course cards list all tiers so you can see the uplift from theory-only to tutor-reviewed and live options.",
      //   includes: [
      //     "Everything in the tutor-reviewed tier",
      //     "Group webinar session on selected courses",
      //     "1:1 live or online theory assessment where included",
      //     "Tier pricing shown on each eligible course card",
      //   ],
      //   ctaLabel: "Browse live session courses",
      //   meta_title: "Online Training With Live Sessions | Cosmetic College",
      //   meta_description:
      //     "Browse online training with live webinar and assessment support. Course cards show theory-only, tutor-reviewed and full pathway tier pricing.",
      // },
    ],
    complianceNote:
      "Online courses are knowledge-based and designed for qualified professionals with relevant prior training or experience. They do not replace practical onsite training or automatically provide insurance eligibility.",
  },
  categoryCards: [
    {
      id: "beauty",
      title: "Online beauty courses",
      shortTitle: "Beauty",
      image: "/shots/eyelash-extensions.jpeg",
      description:
        "Learn in-demand beauty skills online - lashes, brows, waxing and more - with tutor-built training you can start today and a certificate to show for it.",
      ctaLabel: "Browse beauty courses",
      meta_title: "Online Beauty Courses | Learn & Get Certified | Cosmetic College",
      meta_description:
        "Learn beauty skills online - lashes, brows, waxing and more. Tutor-built courses from £29.99, start today, CPD certificate on completion. Pay in 3 with Klarna.",
    },
    {
      id: "aesthetics",
      title: "Online aesthetics courses",
      shortTitle: "Aesthetics",
      image: "/shots/dermaplaning.jpeg",
      description:
        "Build your skin, facial and device treatment knowledge online - tutor-built courses you can start today, with a certificate on completion.",
      ctaLabel: "Browse aesthetics courses",
      meta_title: "Online Aesthetics Courses | Learn & Get Certified | Cosmetic College",
      meta_description:
        "Learn skin, facial and device treatments online. Tutor-built courses from £39.99, start today, CPD certificate on completion. Pay in 3 with Klarna.",
    },
    {
      id: "injectables-advanced",
      title: "Online injectables & advanced courses",
      shortTitle: "Injectables",
      image: "/shots/foundation-anti-wrinkle.jpeg",
      description:
        "Grow into injectables and advanced treatments with tutor-built online courses covering the knowledge behind each procedure - start today, certificate on completion.",
      ctaLabel: "Browse injectables courses",
      meta_title: "Online Injectables & Advanced Courses | Cosmetic College",
      meta_description:
        "Learn the theory behind injectables and advanced aesthetic treatments online. Tutor-built courses, start today, CPD certificate on completion. Pay in 3 with Klarna.",
    },
    {
      id: "vtct-online-theory",
      title: "Online VTCT courses",
      shortTitle: "VTCT",
      image: "/images/courses/anatomy-physiology-and-pathology.jpg",
      description:
        "Earn a recognised VTCT award online, at your own pace - the qualification step into higher-level aesthetics, no previous experience required.",
      ctaLabel: "Browse VTCT courses",
      meta_title: "Online VTCT Courses | Recognised Awards | Cosmetic College",
      meta_description:
        "Browse VTCT online theory courses for progression into regulated practical qualifications in beauty and aesthetics.",
    },
    {
      id: "bundles",
      title: "Online training bundles",
      shortTitle: "Bundles",
      image: "/images/courses/master-aesthetics-package.jpg",
      description:
        "Save with multi-course bundles across complementary treatments - the fastest way to build a wider service menu.",
      ctaLabel: "Browse bundles",
      meta_title: "Online Training Bundles | Cosmetic College",
      meta_description:
        "Save with bundled online training across complementary beauty and aesthetics treatments. Multi-buy discounts and tier pricing on eligible courses.",
    },
  ],
  featuredSlugs: [
    "vtct-online-training-bundle",
    "vtct-level-3-award-in-anatomical-and-physiological-knowledge-of-body",
    "online-polynucleotides-training",
    "online-foundation-anti-wrinkle-injections-training-course",
    "microneedling",
    "chemical-skin-peels",
    "vitamin-drip-and-injections-online-training-bundle",
  ],
  featured: {
    eyebrow: "Online training",
    title: "Popular online courses",
    description:
      "Hand-picked courses and bundles for practitioners building theory before practical training or the next qualification step.",
  },
  bestSellers: {
    eyebrow: "Best sellers",
    title: "What practitioners buy first",
    description:
      "Popular theory refreshers, in-demand treatments and bundles, chosen when you know what you want to learn and need access today.",
  },
  bestSellerSlugs: [
    "vtct-level-3-award-in-anatomical-and-physiological-knowledge-of-body",
    "online-polynucleotides-training",
    "online-foundation-anti-wrinkle-injections-training-course",
    "microneedling",
    "dermaplaning",
    "chemical-skin-peels",
    "brow-lamination",
    "skin-booster-online-training-bundle",
  ],
  proof: {
    title: "Why people buy online training from Cosmetic College",
    cards: [
      {
        title: "Study when it works for you",
        body: "Choose online training when you want structured learning without waiting for the next practical intake.",
      },
      {
        title: "Learn from treatment-led educators",
        body: "Get training built by tutors working inside practical beauty and aesthetics education, not generic course marketplaces.",
      },
      {
        title: "Use online training to support progression",
        body: "Strengthen theory, refresh treatment knowledge, or prepare for the next stage in your wider training plan.",
      },
    ],
    testimonial: {
      quote:
        "The e-learning was straightforward and easy to get through, and it actually helped me feel more prepared before practical training.",
      author: "Cosmetic College student review",
    },
  },
  howItWorks: {
    eyebrow: "How online training works",
    title: "Pick your tier, checkout once, and know exactly what happens next",
    description:
      "Eligible courses show theory-only and tutor-reviewed pricing on the same card - compare before you buy instead of discovering upgrades later.",
    tiers: [
      {
        id: "theory",
        title: "Theory only",
        priceHint: "From £39.99",
        summary:
          "On-demand lessons with a CPD or VTCT theory certificate on completion. No portfolio submission.",
      },
      {
        id: "case_study",
        title: "Theory + tutor review",
        priceHint: "From £99",
        summary:
          "Full theory access plus online case study submission, tutor feedback within around 5 working days, and certification that reflects review.",
      },
      // {
      //   id: "live",
      //   title: "Full online pathway",
      //   priceHint: "From £149",
      //   summary:
      //     "Everything in the tutor-reviewed tier, plus group webinar and live or theory assessment support where included on the course.",
      // },
    ],
    pricingExample:
      "Typical course card pricing: Theory only £49.99 · Theory + tutor-reviewed case study £129.99. Exact tiers and prices vary by course.",
    injectablesNote:
      "For injectables and advanced practitioner topics we use portfolio review and theory assessment wording. These confirm knowledge-based learning, not practical competence or treatment sign-off.",
    steps: [
      {
        title: "Compare tiers on the course card",
        body: "Browse by treatment area or training pathway. Eligible courses list all tier prices side by side before you add to basket.",
      },
      {
        title: "Buy the level that fits today",
        body: "Secure checkout with same-day access, plus 3 eligible online courses for the price of 2 - your lowest-priced course free.",
      },
      {
        title: "Work through tutor-built lessons",
        body: "Study on demand with demonstration-led modules, protocols, and treatment context from educators who deliver practical training every week.",
      },
      {
        title: "Complete, submit, and certify",
        body: "Theory-only routes certify on completion. Tutor-reviewed tiers add portfolio submission and review before release - feedback typically within five working days.",
      },
    ],
  },
  supportPanel: {
    eyebrow: "Need help choosing?",
    title: "Not sure which online training is right for you?",
    description:
      "Speak to admissions if you want to compare online training with onsite options, check suitability, or plan the next stage after completion.",
    primaryCta: {
      href: "/request-a-callback/",
      label: "Speak to admissions",
    },
    secondaryCta: {
      href: "/pathway-to-aesthetics/",
      label: "View progression guidance",
    },
    guideCta: {
      href: "/blog/online-vs-in-person-aesthetics-training-uk/",
      label: "Compare online and in-person training",
    },
  },
} as const;
